/**
 * Editing aids only; the backend remains the authority. One grammar, two
 * dialects: the Number chart's formula (one value over the range, every signal
 * inside a summary) and a derived signal's formula (a value on every row, bare
 * signals allowed, summaries optionally over a trailing window).
 */
import { rankMentions } from '../assistant/mentions.ts';
import type { MentionSegment } from '../assistant/mentions.ts';
import { fuzzyScore } from '../../utils/fuzzy.ts';
import { NUMBER_AGGS } from './number-config.ts';

export interface FormulaDialect {
  kind: 'number' | 'derived';
  functions: ReadonlyArray<{ name: string; label: string }>;
}

export const NUMBER_DIALECT: FormulaDialect = {
  kind: 'number',
  functions: NUMBER_AGGS.map((item) => ({ name: item.value.toUpperCase(), label: item.label })),
};

/** Mirror of `WINDOW_FUNCS` in backend/filter_expr.py - change both together. */
export const DERIVED_DIALECT: FormulaDialect = {
  kind: 'derived',
  functions: [
    { name: 'AVG', label: 'Average' },
    { name: 'MIN', label: 'Minimum' },
    { name: 'MAX', label: 'Maximum' },
    { name: 'SUM', label: 'Sum' },
    { name: 'COUNT', label: 'Count of numeric values' },
    { name: 'STDDEV', label: 'Standard deviation' },
  ],
};

/** Mirror of the backend's duration units and 31-day cap. */
const DURATION_RE = /^\d+(?:min|s|m|h|d)(?![A-Za-z0-9_])/;
const DURATION_SECONDS: Record<string, number> = { s: 1, m: 60, min: 60, h: 3600, d: 86400 };
const MAX_WINDOW_SECONDS = 31 * 86400;

const isFunctionIn = (dialect: FormulaDialect, name: string): boolean =>
  dialect.functions.some((item) => item.name === name.toUpperCase());
interface Token { kind: 'name' | 'string' | 'number' | 'duration' | 'symbol'; value: string; start: number; end: number }

function quoted(text: string, start: number): { value: string; end: number } {
  const quote = text[start];
  let value = '';
  for (let i = start + 1; i < text.length; i++) {
    const char = text[i]!;
    if (char === quote) return { value, end: i + 1 };
    if (char === '\n' || char === '\r') throw Error('Signal names cannot contain an unescaped newline.');
    if (char !== '\\') { value += char; continue; }
    const escaped = text[++i];
    if (escaped === undefined) break;
    const simple: Record<string, string> = { n: '\n', r: '\r', t: '\t', b: '\b', f: '\f', v: '\v', a: '\x07', '\\': '\\', '"': '"', "'": "'" };
    if (escaped in simple) value += simple[escaped];
    else if (escaped === 'u' || escaped === 'x' || escaped === 'U') {
      const length = escaped === 'x' ? 2 : escaped === 'U' ? 8 : 4;
      const digits = text.slice(i + 1, i + 1 + length);
      if (digits.length !== length || !/^[\da-f]+$/i.test(digits)) throw Error('Invalid escape in signal name.');
      value += String.fromCodePoint(parseInt(digits, 16)); i += length;
    } else value += `\\${escaped}`;
  }
  throw Error('Close the quote around the signal name.');
}

function tokenize(text: string, tolerant = false, dialect: FormulaDialect = NUMBER_DIALECT): Token[] {
  const tokens: Token[] = [];
  let parentheses = 0;
  for (let i = 0; i < text.length;) {
    if (/\s/.test(text[i]!)) {
      if (!tolerant && /[\r\n]/.test(text[i]!) && parentheses === 0) {
        throw Error('Keep the formula on one line, or put line breaks inside parentheses.');
      }
      i++; continue;
    }
    const start = i;
    if (dialect.kind === 'derived' && text[i] === "'") {
      if (tolerant) break;
      throw Error('Put signal names in double quotes, e.g. "Fuel rate".');
    }
    if (dialect.kind === 'derived' && text[i] === '[') {
      // The old expression syntax; still read so saved formulas keep working.
      const close = text.indexOf(']', i);
      if (close < 0 || /[\r\n]/.test(text.slice(i, close))) {
        if (tolerant) break;
        throw Error('Close the [ ] around the signal name.');
      }
      i = close + 1;
      tokens.push({ kind: 'string', value: text.slice(start + 1, close).trim(), start, end: i }); continue;
    }
    const duration = dialect.kind === 'derived' ? DURATION_RE.exec(text.slice(i)) : null;
    if (duration) {
      i += duration[0].length;
      tokens.push({ kind: 'duration', value: duration[0], start, end: i }); continue;
    }
    if (text[i] === '"' || text[i] === "'") {
      try {
        const token = quoted(text, i); i = token.end;
        tokens.push({ kind: 'string', value: token.value, start, end: i }); continue;
      } catch (error) { if (tolerant) break; throw error; }
    }
    const number = /^(?:0[xX][\da-fA-F]+|0[bB][01]+|0[oO][0-7]+|(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?)/.exec(text.slice(i));
    const name = /^[A-Za-z_]\w*/.exec(text.slice(i));
    if (number || name) {
      const value = (number ?? name)![0]; i += value.length;
      tokens.push({ kind: number ? 'number' : 'name', value, start, end: i }); continue;
    }
    if (!'+-*/()'.includes(text[i]!) && !(dialect.kind === 'derived' && text[i] === ',')) {
      if (tolerant) break;
      throw Error(dialect.kind === 'derived'
        ? 'Use signals, summary functions, numbers, parentheses and + − × ÷.'
        : 'Use summary functions, numbers, parentheses and + − × ÷.');
    }
    if (text[i] === '(') parentheses++;
    if (text[i] === ')') parentheses--;
    tokens.push({ kind: 'symbol', value: text[i++]!, start, end: i });
  }
  return tokens;
}

/** A small recursive-descent parser prevents malformed and unknown references. */
export function validateNumberFormula(
  text: string, known?: (name: string) => boolean, dialect: FormulaDialect = NUMBER_DIALECT,
): string | null {
  const derived = dialect.kind === 'derived';
  if (!text.trim()) return derived ? 'Write a formula using signals.' : 'Choose a summary function and a signal.';
  if (text.length > 1000) return 'Keep the formula within 1,000 characters.';
  const isFunction = (name: string): boolean => isFunctionIn(dialect, name);
  const names = dialect.functions.map((item) => item.name);
  const functionList = `${names.slice(0, -1).join(', ')} or ${names.at(-1)}`;
  try {
    const tokens = tokenize(text.trim(), false, dialect);
    let pos = 0, nodes = 1, depth = 0, aggregates = 0;
    const peek = (): Token | undefined => tokens[pos];
    const take = (value: string): boolean => {
      if (peek()?.value !== value || peek()?.kind !== 'symbol') return false;
      pos++; return true;
    };
    const expect = (value: string): void => { if (!take(value)) throw Error(`Add ${value === ')' ? 'a closing parenthesis' : 'an opening parenthesis'}.`); };
    const atom = (): void => {
      if (++depth > 100 || nodes > 100) throw Error('Simplify the formula; it is too complex.');
      try {
        if (take('+') || take('-')) { nodes += 2; atom(); return; }
        if (take('(')) { expression(); expect(')'); return; }
        const token = peek();
        if (token?.kind === 'number') {
          if (!Number.isFinite(Number(token.value))) throw Error('Use a finite number.');
          nodes++; pos++; return;
        }
        const signalName = (signal: Token | undefined): string => {
          if (!signal || !['name', 'string'].includes(signal.kind) || !signal.value) {
            throw Error('Choose one signal inside the summary function.');
          }
          if (signal.value.length > 250) throw Error('Signal names must be at most 250 characters.');
          if (known && !known(signal.value)) throw Error(`Unknown numeric signal: ${signal.value}. Choose it from the suggestions.`);
          return signal.value;
        };
        const callsFunction = token?.kind === 'name' && tokens[pos + 1]?.value === '(';
        if (derived && token && !callsFunction && ['name', 'string'].includes(token.kind)) {
          signalName(token); pos++; nodes++; aggregates++; return;
        }
        if (token?.kind !== 'name' || !isFunction(token.value)) {
          throw Error(callsFunction ? `Unknown function ${token!.value}. Use ${functionList}.`
            : derived ? 'Choose a signal, a summary function or a number.'
              : `Choose ${functionList}, or enter a number.`);
        }
        const fn = token.value.toUpperCase();
        pos++; expect('(');
        const signal = peek();
        if (signal?.kind === 'name' && tokens[pos + 1]?.value === '(') throw Error('Choose one signal inside the summary function.');
        signalName(signal);
        pos++;
        if (derived && take(',')) {
          const window = peek();
          if (window?.kind !== 'duration') throw Error(`The window is a duration such as 10m, 1h or 7d, e.g. ${fn}("STW", 1h).`);
          const unit = window.value.replace(/^\d+/, '');
          const seconds = Number(window.value.slice(0, -unit.length)) * DURATION_SECONDS[unit]!;
          if (seconds < 1 || seconds > MAX_WINDOW_SECONDS) throw Error('A window must be between 1s and 31d.');
          pos++; nodes++;
        }
        expect(')'); nodes += signal!.kind === 'string' ? 4 : 5; aggregates++;
      } finally { depth--; }
    };
    const product = (): void => { atom(); while (take('*') || take('/')) { nodes += 2; atom(); } };
    const expression = (): void => { product(); while (take('+') || take('-')) { nodes += 2; product(); } };
    expression();
    if (pos !== tokens.length) {
      throw Error(derived ? 'Put +, −, × or ÷ between terms; a function takes one signal and an optional window.'
        : 'Put +, −, × or ÷ between summaries; each function takes one signal.');
    }
    if (!aggregates) throw Error(derived ? 'Use at least one signal.' : 'Include at least one signal summary.');
    if (nodes > 100) throw Error('Simplify the formula; it is too complex.');
    return null;
  } catch (error) { return error instanceof Error ? error.message : 'Check the formula.'; }
}

/**
 * Evaluate a Number formula with `summary` giving each aggregate's value - the
 * browser's twin of the server's `evaluate_number_expression`, for a gauge dial
 * recomputed under a slicer. Null when any term is null, on division by zero,
 * or when the result is not finite - as there. Assumes a formula that
 * `validateNumberFormula` accepts; anything else is null.
 */
export function evaluateNumberFormula(
  text: string, summary: (fn: string, signal: string) => number | null,
): number | null {
  try {
    const tokens = tokenize(text.trim());
    let pos = 0;
    const take = (value: string): boolean => {
      if (tokens[pos]?.value !== value || tokens[pos]?.kind !== 'symbol') return false;
      pos++; return true;
    };
    const atom = (): number | null => {
      if (take('-')) { const v = atom(); return v == null ? null : -v; }
      if (take('+')) return atom();
      if (take('(')) { const v = expression(); if (!take(')')) throw Error(); return v; }
      const token = tokens[pos++];
      if (token?.kind === 'number') return Number(token.value);
      if (token?.kind !== 'name' || !take('(')) throw Error();
      const signal = tokens[pos++];
      if (!signal || !take(')')) throw Error();
      return summary(token.value.toLowerCase(), signal.value);
    };
    const product = (): number | null => {
      let v = atom();
      for (;;) {
        const mul = take('*');
        if (!mul && !take('/')) return v;
        const r = atom();
        if (v == null || r == null) { v = null; continue; }
        if (!mul && r === 0) throw Error();
        v = mul ? v * r : v / r;
      }
    };
    const expression = (): number | null => {
      let v = product();
      for (;;) {
        const add = take('+');
        if (!add && !take('-')) return v;
        const r = product();
        v = v == null || r == null ? null : add ? v + r : v - r;
      }
    };
    const value = expression();
    if (pos !== tokens.length) return null;
    return value != null && Number.isFinite(value) ? value : null;
  } catch { return null; }
}

export interface FormulaCompletion {
  kind: 'signal' | 'function'; start: number; end: number; query: string; inFunction: boolean;
}
export interface FormulaSuggestion { kind: 'signal' | 'function'; name: string; label: string }

/** Locate the editable argument or function at the caret, ignoring quoted text. */
export function numberFormulaCompletion(
  text: string, caret: number, dialect: FormulaDialect = NUMBER_DIALECT,
): FormulaCompletion | null {
  const isFunction = (name: string): boolean => isFunctionIn(dialect, name);
  const prefix = text.slice(0, caret);
  let masked = '', quote = '', quoteStart = -1;
  for (let i = 0; i < prefix.length; i++) {
    const char = prefix[i]!;
    if (quote) {
      masked += ' ';
      if (char === '\\') { masked += ' '; i++; }
      else if (char === quote) { quote = ''; quoteStart = -1; }
    } else if (char === '"' || char === "'") { quote = char; quoteStart = i; masked += ' '; }
    else masked += char;
  }
  const stack: Array<{ start: number; call: boolean }> = [];
  for (let i = 0; i < masked.length; i++) {
    if (masked[i] === '(') {
      const name = /([A-Za-z_]\w*)\s*$/.exec(masked.slice(0, i))?.[1] ?? '';
      stack.push({ start: i + 1, call: isFunction(name) });
    } else if (masked[i] === ')') stack.pop();
  }
  const frame = stack.at(-1);
  if (frame?.call) {
    const raw = prefix.slice(frame.start);
    if (!quote && /^\s*["']/.test(raw)) return null; // a complete quoted argument
    if (!quote && /[()+*/,]/.test(raw)) return null;
    const start = frame.start + (raw.match(/^\s*/)?.[0].length ?? 0);
    let end = caret;
    if (quoteStart >= 0) {
      try { end = quoted(text, quoteStart).end; } catch { /* unfinished name */ }
    } else {
      while (end < text.length && !/[()+*/\r\n]/.test(text[end]!)) end++;
      while (end > caret && /\s/.test(text[end - 1]!)) end--;
    }
    return { kind: 'signal', start, end, query: raw.trimStart().replace(/^[@"']/, ''), inFunction: true };
  }
  if (quote) {
    // A derived formula takes a quoted signal anywhere a value fits.
    if (dialect.kind !== 'derived') return null;
    let end = caret;
    try { end = quoted(text, quoteStart).end; } catch { /* unfinished name */ }
    return { kind: 'signal', start: quoteStart, end, query: prefix.slice(quoteStart + 1), inFunction: false };
  }
  const mention = /@([^@()+*/\r\n]*)$/.exec(prefix);
  if (mention && (!prefix[mention.index - 1] || /[\s(,+*/-]/.test(prefix[mention.index - 1]!))) {
    let end = caret;
    while (end < text.length && !/[()+*/\r\n]/.test(text[end]!)) end++;
    return { kind: 'signal', start: mention.index, end, query: mention[1]!, inFunction: false };
  }
  const fragment = /[A-Za-z_]\w*$/.exec(prefix);
  const start = fragment?.index ?? caret;
  if (!/[+*/(-]$/.test(prefix.slice(0, start).trimEnd()) && prefix.slice(0, start).trim()) return null;
  if (fragment && /\d$/.test(prefix.slice(0, start))) return null; // a number or a window such as 10min
  let end = caret;
  while (end < text.length && /\w/.test(text[end]!)) end++;
  return { kind: 'function', start, end, query: fragment?.[0] ?? '', inFunction: false };
}

export function numberFormulaSuggestions(
  completion: FormulaCompletion, columns: readonly string[], dialect: FormulaDialect = NUMBER_DIALECT,
): FormulaSuggestion[] {
  const signals = (limit: number): FormulaSuggestion[] => rankMentions(completion.query,
    columns.map((name) => ({ kind: 'signal', id: name, label: name })), limit)
    .map((item) => ({ kind: 'signal', name: item.id, label: item.label }));
  if (completion.kind === 'signal') return signals(8);
  const scored = dialect.functions.map((item) => ({ ...item, score: completion.query
    ? fuzzyScore(completion.query, `${item.name} ${item.label}`) : 0 }))
    .filter((item): item is { name: string; label: string; score: number } => item.score !== null);
  scored.sort((a, b) => b.score - a.score);
  const functions = scored.map((item): FormulaSuggestion => ({ kind: 'function', name: item.name, label: item.label }));
  // A derived formula takes a bare signal wherever a function fits; once a few
  // letters are typed the signals usually matter more than the six functions.
  if (dialect.kind !== 'derived') return functions;
  if (!completion.query) return [...functions, ...signals(6)];
  const exact = functions.filter((item) => item.name.startsWith(completion.query.toUpperCase()));
  return [...exact, ...signals(8), ...functions.filter((item) => !exact.includes(item))].slice(0, 10);
}

export function applyNumberFormulaSuggestion(
  text: string, completion: FormulaCompletion, item: FormulaSuggestion, dialect: FormulaDialect = NUMBER_DIALECT,
): { text: string; caret: number } {
  let insert: string, caret: number;
  const suffix = text.slice(completion.end);
  if (item.kind === 'function') {
    insert = `${item.name}${suffix.startsWith('(') ? '' : '()'}`;
    caret = completion.start + item.name.length + 1;
  } else {
    insert = completion.inFunction || dialect.kind === 'derived'
      ? JSON.stringify(item.name) : `SUM(${JSON.stringify(item.name)})`;
    if (completion.inFunction && !suffix.startsWith(')')) insert += ')';
    caret = completion.start + insert.length + (completion.inFunction && suffix.startsWith(')') ? 1 : 0);
  }
  return { text: text.slice(0, completion.start) + insert + suffix, caret };
}

/** Highlight complete signal arguments without colouring names inside other names. */
export function numberFormulaSegments(
  text: string, known: (name: string) => boolean, dialect: FormulaDialect = NUMBER_DIALECT,
): MentionSegment[] {
  const isFunction = (name: string): boolean => isFunctionIn(dialect, name);
  const tokens = tokenize(text, true, dialect);
  const segments: MentionSegment[] = [];
  let start = 0;
  tokens.forEach((token, i) => {
    if (!known(token.value) || !['string', 'name'].includes(token.kind)) return;
    const argument = tokens[i - 1]?.value === '(' && isFunction(tokens[i - 2]?.value ?? '');
    const bare = dialect.kind === 'derived' && tokens[i + 1]?.value !== '(';
    if (!argument && !bare) return;
    if (token.start > start) segments.push({ text: text.slice(start, token.start) });
    segments.push({ text: text.slice(token.start, token.end), kind: 'signal' });
    start = token.end;
  });
  if (start < text.length) segments.push({ text: text.slice(start) });
  return segments;
}
